import React, { useCallback, useMemo, useState } from 'react';

import {
  ALINE_TOOL_NAMES,
  resolveAiReportFileFields,
  ResolvedReportPlanSchema,
} from '@aline/common';
import type { ReportGridFilterModel, ResolvedReportPlan } from '@aline/common';
import { Typography } from '@mui/material';
import { getTypographyForSx } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

import { ApprovalCard } from './AskUser';
import { DocumentFilterControls } from './question-plan/DocumentFilterControls';
import {
  countRunnableColumns,
  getIncludedColumns,
  toggleExcludedIndex,
} from './question-plan/question-plan-helpers';
import { QuestionColumnsEditor } from './question-plan/QuestionColumnsEditor';
import { QuestionPlanActions } from './question-plan/QuestionPlanActions';
import {
  QuestionPlanApprovalCard,
  QuestionPlanHeader,
  QuestionPlanSection,
} from './question-plan/QuestionPlanApprovalCard';
import { RunSummary } from './question-plan/RunSummary';
import { useSubmitApprovalResponse } from './question-plan/use-submit-approval-response';
import { buildReportPlanApprovalResponse } from './report-plan-approval-helpers';
import type { ReportPlanAction } from './report-plan-approval-helpers';
import { ReportPlanFileFieldRow } from './ReportPlanFileFieldRow';
import { useMatchingDocumentCount } from './useMatchingDocumentCount';

const CARD_TITLE = 'AI wants to create a report';

const useStyles = makeStyles()(theme => ({
  matchingDocuments: {
    ...getTypographyForSx(theme.typography.body2),
    color: theme.appColors.secondaryType,
    fontSize: 12,
    whiteSpace: 'nowrap',
  },
}));

interface ReportPlanApprovalCardProps {
  approvalId: string;
  input: unknown;
  messageId: string;
  maxHeight?: number | null;
}

/**
 * The plan the user decides on before any report is created: its goal, the
 * columns it will have, and the documents it will cover. Unlike the other
 * approval cards this one is editable in place, because a report's coverage
 * is the whole point of it: the user can leave out extraction columns and
 * change filters, and what they approve is what gets created.
 *
 * The card decides this plan and nothing else. Wanting a different plan is a
 * chat message, which supersedes the pending approval and starts a new turn.
 */
export function ReportPlanApprovalCard({
  approvalId,
  input,
  maxHeight,
  messageId,
}: ReportPlanApprovalCardProps) {
  const parsed = ResolvedReportPlanSchema.safeParse(input);
  if (!parsed.success)
    return (
      <ApprovalCard
        approvalId={approvalId}
        input={input}
        maxHeight={maxHeight}
        messageId={messageId}
        toolName={ALINE_TOOL_NAMES.createReportTool}
      />
    );

  /**
   * Keyed by approval so a later plan, which arrives as a new approval on the
   * same message, seeds fresh edit state instead of inheriting the edits made
   * to the plan it replaced.
   */
  return (
    <ReportPlanApprovalCardReady
      key={approvalId}
      approvalId={approvalId}
      maxHeight={maxHeight}
      messageId={messageId}
      plan={parsed.data}
    />
  );
}

/** "Matching 143 documents", once the count lands. */
const MatchingDocuments = React.memo(function MatchingDocuments({
  matchingDocuments,
}: {
  matchingDocuments: number | undefined;
}) {
  const { classes } = useStyles();
  if (matchingDocuments === undefined) return null;
  return (
    <Typography className={classes.matchingDocuments}>
      Matching <b>{matchingDocuments.toLocaleString()} documents</b>
    </Typography>
  );
});

function ReportPlanApprovalCardReady({
  approvalId,
  maxHeight,
  messageId,
  plan,
}: {
  approvalId: string;
  messageId: string;
  plan: ResolvedReportPlan;
  maxHeight?: number | null;
}) {
  const { submit, submitting } = useSubmitApprovalResponse();
  const [excludedIndices, setExcludedIndices] = useState<Set<number>>(new Set());
  const [filterModel, setFilterModel] = useState<ReportGridFilterModel>(plan.filterModel);
  const fileFieldRows = useMemo(
    () =>
      resolveAiReportFileFields(plan.fileFields).map(field => (
        <ReportPlanFileFieldRow key={field} field={field} />
      )),
    [plan.fileFields]
  );
  const matchingDocuments = useMatchingDocumentCount({ enabled: !submitting, filterModel });
  // What "Create and run" would run: Manual Entry columns are created but never run.
  const questionCount = countRunnableColumns(getIncludedColumns(plan.columns, excludedIndices));
  const counts = useMemo(
    () => ({ documentCount: matchingDocuments ?? 0, questionCount }),
    [matchingDocuments, questionCount]
  );

  const handleToggleExclude = useCallback(
    (index: number) =>
      setExcludedIndices(previous => toggleExcludedIndex(plan.columns, previous, index)),
    [plan.columns]
  );

  const respond = useCallback(
    (action: ReportPlanAction, largeRunConfirmed = false) =>
      void submit(
        buildReportPlanApprovalResponse({
          action,
          approvalId,
          edits: { excludedIndices, filterModel, plan },
          largeRunConfirmed,
          messageId,
        })
      ),
    [approvalId, excludedIndices, filterModel, messageId, plan, submit]
  );
  const createOnly = useMemo(
    () => ({ label: 'Create only', onClick: () => respond('createOnly') }),
    [respond]
  );
  const handleRun = useCallback(
    ({ largeRunConfirmed }: { largeRunConfirmed: boolean }) =>
      respond('createAndRun', largeRunConfirmed),
    [respond]
  );
  const handleSkip = useCallback(() => respond('skip'), [respond]);

  return (
    <QuestionPlanApprovalCard
      editor={
        <QuestionColumnsEditor
          disabled={submitting}
          editableColumns={plan.columns}
          excludedIndices={excludedIndices}
          leadingRows={fileFieldRows}
          onToggleExclude={handleToggleExclude}
        />
      }
      footer={
        <QuestionPlanActions
          counts={counts}
          disabled={submitting}
          leftAction={createOnly}
          primaryDisabledReason={getCreateAndRunDisabledReason({
            aiUsageBlockedReason: plan.aiUsageBlockedReason,
            isCounting: matchingDocuments === undefined,
            questionCount,
          })}
          primaryLabel="Create and run"
          onRun={handleRun}
          onSkip={handleSkip}
        />
      }
      header={
        <>
          <QuestionPlanHeader description={plan.goal} title={`Proposed plan - ${plan.name}`} />
          {Boolean(plan.howToUse) && (
            <QuestionPlanSection label="How to use">{plan.howToUse}</QuestionPlanSection>
          )}
        </>
      }
      listActions={
        <DocumentFilterControls
          disabled={submitting}
          filterModel={filterModel}
          summary={<MatchingDocuments matchingDocuments={matchingDocuments} />}
          onFilterModelChange={setFilterModel}
        />
      }
      listLabel="Columns"
      maxHeight={maxHeight}
      summary={matchingDocuments !== undefined && questionCount > 0 && <RunSummary {...counts} />}
      title={CARD_TITLE}
    />
  );
}

/**
 * Why "Create and run" is off, or undefined when it is on. "Create only"
 * stays available either way: a report costs nothing until it runs.
 */
function getCreateAndRunDisabledReason({
  aiUsageBlockedReason,
  isCounting,
  questionCount,
}: {
  aiUsageBlockedReason: string | undefined;
  /** Held until the count lands, so a large run cannot skip its confirmation. */
  isCounting: boolean;
  questionCount: number;
}) {
  if (aiUsageBlockedReason) return aiUsageBlockedReason;
  if (!questionCount) return 'The plan has no AI columns to run.';
  if (isCounting) return 'Counting the matching documents...';
  return undefined;
}
